// ===================================
// ADMIN DASHBOARD PAGE
// src/pages/AdminDashboard.js
// Updated for AdminLayout (no Navbar wrapper)
// ===================================

import React, { useState, useEffect } from 'react';
import { getAdminStats, getUsers, getRefundRequests, approveRefund, rejectRefund } from '../services/adminApi';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, usersRes, refundsRes] = await Promise.all([
          getAdminStats(),
          getUsers({ limit: 20 }),
          getRefundRequests()
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data.users || []);
        setRefundRequests(refundsRes.data.requests || []);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleApproveRefund = async (id) => {
    try {
      await approveRefund(id, 'Approved by admin');
      setRefundRequests(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      alert('Failed to approve refund');
    }
  };

  const handleRejectRefund = async (id) => {
    try {
      await rejectRefund(id, 'Rejected by admin');
      setRefundRequests(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      alert('Failed to reject refund');
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard-page">
        <div className="loading-container">
          <div className="spinner spinner-lg"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-subtitle">Platform management and analytics</p>
        </div>

        <div className="admin-tabs">
          {['overview', 'users', 'refunds'].map(tab => (
            <button
              key={tab}
              className={`admin-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && stats && (
          <>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div>
                  <div className="stat-value">{stats.users?.total_users || 0}</div>
                  <div className="stat-label">Total Users</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div>
                  <div className="stat-value">{stats.sessions?.total_sessions || 0}</div>
                  <div className="stat-label">Total Sessions</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div>
                  <div className="stat-value">{stats.sessions?.completed_sessions || 0}</div>
                  <div className="stat-label">Completed</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div>
                  <div className="stat-value">${stats.revenue?.total_revenue || 0}</div>
                  <div className="stat-label">Total Revenue</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Interview Types</h3>
              </div>
              <div className="card-body">
                <div className="type-breakdown">
                  {stats.typeBreakdown?.map(type => (
                    <div key={type.interview_type} className="type-item">
                      <span className="capitalize">{type.interview_type}</span>
                      <span>{type.count} sessions</span>
                      <span>Avg: {type.avg_score || '--'}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'users' && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Users ({users.length})</h3>
            </div>
            <div className="card-body">
              <div className="users-table">
                {users.map(user => (
                  <div key={user.id} className="user-row">
                    <div className="user-info">
                      <strong>{user.name || user.username}</strong>
                      <span>{user.email}</span>
                    </div>
                    <div className="user-stats">
                      <span>{user.session_count} sessions</span>
                      <span>Avg: {user.avg_score || '--'}%</span>
                    </div>
                    {user.is_admin && <span className="badge badge-warning">Admin</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'refunds' && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Pending Refunds ({refundRequests.length})</h3>
            </div>
            <div className="card-body">
              {refundRequests.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">✅</div>
                  <h4>No pending refunds</h4>
                </div>
              ) : (
                <div className="refund-list">
                  {refundRequests.map(req => (
                    <div key={req.id} className="refund-item">
                      <div className="refund-info">
                        <strong>{req.user_email}</strong>
                        <p>{req.reason}</p>
                        <span className="capitalize">{req.interview_type} • {req.duration}min • ${req.amount_paid}</span>
                      </div>
                      <div className="refund-actions">
                        <button
                          className="btn btn-sm"
                          style={{ background: 'var(--color-success)', color: 'white' }}
                          onClick={() => handleApproveRefund(req.id)}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ background: 'var(--color-error)', color: 'white' }}
                          onClick={() => handleRejectRefund(req.id)}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
